'use client';

import * as z from 'zod';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useSearchParams } from 'src/routes/hooks';
import { PasswordIcon, SentIcon } from 'src/assets/icons';
import { Form, Field, schemaUtils } from 'src/components/hook-form';

import { FormHead } from '../../components/form-head';
import { sendPasswordResetEmail } from '../../context/firebase';
import { FormReturnLink } from '../../components/form-return-link';
import { getErrorMessage } from '../../utils';

// ----------------------------------------------------------------------

export type ResetPasswordSchemaType = z.infer<typeof ResetPasswordSchema>;

export const ResetPasswordSchema = z.object({
  email: schemaUtils.email({
    error: {
      required: 'E-postadress är obligatorisk',
      invalid: 'Ange en giltig e-postadress',
    },
  }),
});

// ----------------------------------------------------------------------

export function AterstallView() {
  const searchParams = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';

  const [skickatTill, setSkickatTill] = useState<string | null>(null);
  const [felmeddelande, setFelmeddelande] = useState<string | null>(null);

  const defaultValues: ResetPasswordSchemaType = {
    email: emailFromUrl,
  };

  const methods = useForm<ResetPasswordSchemaType>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (emailFromUrl) {
      reset({ email: emailFromUrl });
    }
  }, [emailFromUrl, reset]);

  const watchedEmail = watch('email');
  const returnToLoginHref = watchedEmail
    ? `${paths.LOGGA_IN}?email=${encodeURIComponent(watchedEmail)}`
    : paths.LOGGA_IN;

  const onSubmit = handleSubmit(async (data) => {
    try {
      setFelmeddelande(null);
      await sendPasswordResetEmail({ email: data.email });
      setSkickatTill(data.email);
    } catch (error: any) {
      console.error(error);
      const code = error?.code;
      if (code === 'auth/user-not-found') {
        setFelmeddelande('Ingen användare hittades med den angivna e-postadressen.');
      } else if (code === 'auth/invalid-email') {
        setFelmeddelande('Ogiltig e-postadress.');
      } else if (code === 'auth/too-many-requests') {
        setFelmeddelande('För många förfrågningar. Vänta en stund och försök igen.');
      } else {
        setFelmeddelande(getErrorMessage(error) || 'Ett fel uppstod när återställningslänken skulle skickas.');
      }
    }
  });

  const handleSkickaIgen = () => {
    setSkickatTill(null);
    setFelmeddelande(null);
    reset({ email: '' });
  };

  if (skickatTill) {
    return (
      <>
        <FormHead
          icon={<SentIcon />}
          title="Återställningslänk skickad"
          description={
            <>
              Vi har skickat instruktioner för att återställa ditt lösenord till{' '}
              <strong>{skickatTill}</strong>.
              <br />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Kontrollera din inkorg och eventuell skräppostmapp.
              </Typography>
            </>
          }
        />

        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column', mt: 3 }}>
          <Button
            fullWidth
            size="large"
            variant="outlined"
            onClick={handleSkickaIgen}
          >
            Skicka till en annan e-postadress
          </Button>

          <FormReturnLink href={paths.LOGGA_IN} label="Tillbaka till inloggning" />
        </Box>
      </>
    );
  }

  return (
    <>
      <FormHead
        icon={<PasswordIcon />}
        title="Glömt ditt lösenord?"
        description="Ange e-postadressen kopplad till ditt konto så skickar vi en länk för att återställa lösenordet."
      />

      {!!felmeddelande && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {felmeddelande}
        </Alert>
      )}

      <Form methods={methods} onSubmit={onSubmit}>
        <Box sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
          <Field.Text
            autoFocus={!emailFromUrl}
            name="email"
            label="E-postadress"
            placeholder="namn@exempel.se"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Button
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingIndicator="Skickar länk..."
          >
            Skicka återställningslänk
          </Button>
        </Box>
      </Form>

      <FormReturnLink href={returnToLoginHref} label="Tillbaka till inloggning" />
    </>
  );
}
